import { useState } from "react";

// Gráfico de barras simples em SVG/flex (sem lib): uma barra por ponto, tooltip no hover e
// rótulos do eixo X só em alguns pontos pra não amontoar. `format` formata o valor do tooltip.
export const BarChart = ({ data, valueKey, labelKey = "label", format = (v) => v, color = "#6D4AFE", height = 180, tooltipExtra }) => {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  const labelEvery = Math.max(1, Math.ceil(data.length / 8));
  const gridLines = [1, 0.5, 0];

  return (
    <div className="relative">
      <div className="relative" style={{ height }}>
        {gridLines.map((g) => (
          <div key={g} className="absolute inset-x-0 border-t border-dashed border-gray-100" style={{ top: `${(1 - g) * 100}%` }}>
            <span className="tabular absolute -top-2.5 right-0 bg-white pl-1 text-[10px] text-gray-400">{format(max * g)}</span>
          </div>
        ))}
        <div className="absolute inset-0 flex items-end gap-[3px] pr-12">
          {data.map((d, i) => {
            const v = d[valueKey] || 0;
            const pct = (v / max) * 100;
            const active = hover === i;
            return (
              <div
                key={i}
                className="group relative flex h-full flex-1 items-end"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                <div
                  className="w-full rounded-t-[4px] transition-all duration-300"
                  style={{ height: `${Math.max(pct, v ? 2 : 0.5)}%`, backgroundColor: color, opacity: hover === null || active ? 1 : 0.35 }}
                />
                {active && (
                  <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-lg">
                    <p className="text-[10px] text-gray-300">{d.tooltipLabel || d[labelKey]}</p>
                    <p className="tabular font-semibold">{format(v)}</p>
                    {tooltipExtra && <p className="text-[10px] text-gray-300">{tooltipExtra(d)}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-2 flex gap-[3px] pr-12">
        {data.map((d, i) => (
          <div key={i} className="flex-1 text-center text-[10px] text-gray-400">
            {i % labelEvery === 0 ? d[labelKey] : ""}
          </div>
        ))}
      </div>
    </div>
  );
};

// Barras horizontais com rótulo e valor — pra distribuições (planos, áreas, origem)
export const HBarList = ({ items, total, emptyLabel = "Sem dados" }) => {
  if (!items.length) return <p className="py-6 text-center text-sm text-gray-400">{emptyLabel}</p>;
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <ul className="space-y-3">
      {items.map((item) => {
        const pct = total ? Math.round((item.value / total) * 100) : 0;
        return (
          <li key={item.key}>
            <div className="mb-1 flex items-center justify-between gap-3 text-sm">
              <span className="flex min-w-0 items-center gap-2 text-gray-700">
                <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                <span className="truncate">{item.label}</span>
              </span>
              <span className="tabular flex-shrink-0 text-gray-900">
                <span className="font-semibold">{item.value.toLocaleString("pt-BR")}</span>
                {total ? <span className="ml-1.5 text-xs text-gray-400">{pct}%</span> : null}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(item.value / max) * 100}%`, backgroundColor: item.color }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
};
